import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        // Primary button with AiA Gold
        default: 'bg-aia-gold text-aia-navy hover:bg-aia-gold-dark shadow-sm',
        // Secondary button with AiA Blue
        secondary: 'bg-aia-blue text-white hover:bg-aia-blue-dark shadow-sm',
        // Destructive
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        // Outline
        outline: 'border border-aia-blue text-aia-blue bg-transparent hover:bg-aia-blue/10',
        // Ghost
        ghost: 'hover:bg-muted hover:text-foreground',
        // Link
        link: 'text-aia-blue underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        xl: 'h-12 rounded-md px-10 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
